import { Image } from 'src/components/ui/Image'
import { Link } from 'gatsby'
import { sendEvent, makeEventTrack } from 'src/utils/restructure/analytics'
import { InView } from 'react-intersection-observer'
import type {
  NavigateBySportsProps,
  Sports,
} from 'src/components/department/types/navigate-by-sport'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'

import { useNavigateBySports } from './useNavigateBySports'
import './navigateBySports.scss'

export function NavigateBySports(data: NavigateBySportsProps) {
  const { validateProps } = useNavigateBySports()
  const { pageData } = useGlobalContext()
  const propsIsValid = validateProps(data)

  if (!propsIsValid) {
    return <></>
  }

  const {
    enableSession,
    sessionTitle,
    image,
    sports,
    section,
    locationOnPage,
  } = data

  const sendPromoFeatureViewedEvent = () => {
    if (!locationOnPage || !section) {
      return
    }

    const eventData = {
      ...getStandardObject(pageData),
      ...getFeatureObject({
        section,
        locationOnPage,
        name: sessionTitle.text ?? 'N/A',
      }),
    }

    dispatchAmplitudeEvent({ eventName: 'Promo Feature Viewed', eventData })
  }

  const standardObject = getStandardObject(pageData)

  if (!enableSession) {
    return <></>
  }

  const sendAmplitudeClickEvent = (sport: Sports, index: number) => {
    if (!locationOnPage || !section) {
      return
    }

    const featureObject = getFeatureObject({
      section,
      locationOnPage,
      name: sessionTitle?.text ?? sport?.text ?? '',
      index,
    })

    dispatchAmplitudeEvent({
      eventName: 'Promotional Element Selected',
      eventData: {
        ...standardObject,
        ...featureObject,
      },
    })
  }

  return (
    <InView
      as="div"
      threshold={0.7}
      onChange={(inView) => {
        if (inView) {
          sendPromoFeatureViewedEvent()
        }
      }}
      triggerOnce
      role="region"
      aria-label="sessão navegue por esportes"
      data-testid="navigate-for-sports"
      className="mx-auto relative pb-[55px] restructure-small-desktop:pb-0"
    >
      <div className="relative">
        <Image
          data-testid="image-sport"
          src={image.mobile}
          alt={sessionTitle.text}
          width={360}
          height={120}
          sizes="(max-width: 767px) 100vw, 1px"
          className="w-full restructure-tablet:hidden"
        />
        <Image
          data-testid="image-sport"
          src={image.tablet}
          alt={sessionTitle.text}
          width={834}
          height={124}
          sizes="(min-width: 768px) and (max-width: 1023px) 100vw, 1px"
          className="w-full hidden restructure-tablet:block restructure-small-desktop:hidden"
        />
        <Image
          data-testid="image-sport"
          src={image.desktop}
          alt={sessionTitle.text}
          width={1440}
          height={212}
          sizes="(min-width: 1024px) 100vw, 1px"
          className="w-full hidden restructure-small-desktop:block"
        />
        <div className="navigation-sports absolute inset-0 flex flex-col text-center justify-center text-white">
          <h1
            data-testid="title-sport"
            className={`mobile-heading-title3 tablet-heading-title3 desktop-heading-title1 mt-0 mb-0 restructure-small-desktop:mb-6 ${
              sessionTitle?.textColor && sessionTitle.textColor === 'branco'
                ? 'text-restructure-primary-inverted'
                : 'text-restructure-primary'
            }`}
          >
            {sessionTitle.text}
          </h1>
          {sports.length && (
            <ul
              data-testid="list-sports"
              className="navigation-sports flex px-4 restructure-small-desktop:pl-0 restructure-small-desktop:justify-center gap-4 absolute restructure-small-desktop:relative bottom-[-79px] restructure-small-desktop:bottom-0 overflow-x-scroll overflow-y-hidden restructure-small-desktop:overflow-hidden py-4 restructure-small-desktop:py-0 w-full"
            >
              {sports.map((sport, index) => (
                <li data-testid="link-sport" className="flex" key={sport.text}>
                  <Link
                    className="border border-solid border-restructure-border-secondary whitespace-nowrap px-3 py-2 text-restructure-secondary desktop-body-regular-text3 bg-restructure-background-secondary rounded-round hover:bg-restructure-background-tertiary active:bg-restructure-background-disabled"
                    to={sport.linkUrl}
                    onClick={() => {
                      sendAmplitudeClickEvent(sport, index)
                      sendEvent(
                        makeEventTrack({
                          eventAction: `${sessionTitle.text} - ${sport.text}`,
                          eventPage: 'Pagina de departamentos',
                        })
                      )
                    }}
                  >
                    <h2 className="m-0 desktop-body-regular-text3">
                      {sport.text}
                    </h2>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </InView>
  )
}
